import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ArrowLeft, Gamepad2 } from 'lucide-react';
import MFAVerification from '../components/MFAVerification';
import LanguageSwitcher from '../components/LanguageSwitcher';

// SlotFleet Gold Logo Component - Slot Machine Style
const SlotFleetLogo = ({ className = "w-10 h-10" }) => (
  <svg viewBox="0 0 100 100" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fbbf24" />
        <stop offset="50%" stopColor="#f59e0b" />
        <stop offset="100%" stopColor="#d97706" />
      </linearGradient>
    </defs>
    <rect x="10" y="15" width="80" height="70" rx="8" fill="#1a1a1a" stroke="url(#goldGradient)" strokeWidth="3"/>
    <rect x="18" y="28" width="18" height="24" rx="3" fill="#0a0a0a" stroke="url(#goldGradient)" strokeWidth="1.5"/>
    <rect x="41" y="28" width="18" height="24" rx="3" fill="#0a0a0a" stroke="url(#goldGradient)" strokeWidth="1.5"/>
    <rect x="64" y="28" width="18" height="24" rx="3" fill="#0a0a0a" stroke="url(#goldGradient)" strokeWidth="1.5"/>
    <text x="27" y="46" fill="url(#goldGradient)" fontSize="16" fontWeight="bold" fontFamily="Arial">7</text>
    <text x="50" y="46" fill="url(#goldGradient)" fontSize="16" fontWeight="bold" fontFamily="Arial">7</text>
    <text x="73" y="46" fill="url(#goldGradient)" fontSize="16" fontWeight="bold" fontFamily="Arial">7</text>
    <rect x="25" y="62" width="50" height="12" rx="6" fill="url(#goldGradient)"/>
    <circle cx="85" cy="45" r="8" fill="url(#goldGradient)"/>
    <path d="M82 45 L88 45 M85 42 L85 48" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const LoginPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // MFA state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaMethod, setMfaMethod] = useState(null);
  const [tempToken, setTempToken] = useState(null);
  
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Collect all validation errors
    const errors = [];
    if (!email?.trim()) errors.push('Email is required');
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push('Please enter a valid email address');
    }
    if (!password) errors.push('Password is required');
    
    // Show all errors at once
    if (errors.length > 0) {
      toast.error(
        <div>
          <strong>Please fix the following:</strong>
          <ul className="list-disc ml-4 mt-1">
            {errors.map((err, i) => <li key={`err-${i}`}>{err}</li>)}
          </ul>
        </div>,
        { duration: 5000 }
      );
      return;
    }

    setLoading(true);
    try {
      const result = await login(email, password);
      
      // Check if MFA is required
      if (result?.mfa_required) {
        setMfaRequired(true);
        setMfaMethod(result.mfa_method);
        setTempToken(result.temp_token);
        toast.info('Please complete two-factor authentication');
      } else {
        toast.success('Welcome back!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : Array.isArray(error.response?.data?.detail) ? error.response.data.detail.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASuccess = async (accessToken, deviceToken) => {
    try {
      // Store device token in localStorage for trusted device persistence
      if (deviceToken) {
        localStorage.setItem('slotfleet_device_token', deviceToken);
      }
      await loginWithToken(accessToken);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Login failed');
    }
  };

  const handleMFACancel = () => {
    setMfaRequired(false);
    setMfaMethod(null);
    setTempToken(null);
    setPassword('');
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <SEO
        title="Login"
        description="Sign in to your SlotFleet skill games management account. Access your dashboard, track collections, manage inventory, and optimize routes."
        canonical="https://slotfleet.com/login"
        noindex={true}
      />
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-yellow-500/10 rounded-full blur-[120px]" />
      </div>
      
      <div className="w-full max-w-md relative z-10">
        {/* Back to home */}
        <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-amber-400 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        {/* MFA Verification Screen */}
        {mfaRequired ? (
          <MFAVerification
            tempToken={tempToken}
            mfaMethod={mfaMethod}
            onSuccess={handleMFASuccess}
            onCancel={handleMFACancel}
          />
        ) : (
        /* Login Form */
        <div className="bg-neutral-900 rounded-2xl shadow-xl border border-amber-500/20 p-8">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="login-home-link">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
                <Gamepad2 className="w-7 h-7 text-black" />
              </div>
              <div>
                <span className="font-bold text-3xl text-amber-400">Slot</span>
                <span className="font-bold text-3xl text-white">Fleet</span>
              </div>
            </a>
          </div>

          <h2 className="text-2xl font-bold text-center text-white mb-2">{t('auth.welcomeBack')}</h2>
          <p className="text-center text-gray-400 mb-6">{t('auth.loginSubtitle')}</p>
          
          <div className="flex justify-center mb-4">
            <LanguageSwitcher variant="full" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-300">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 bg-black border-amber-500/30 text-white placeholder:text-gray-500 focus:border-amber-500 focus:ring-amber-500"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 bg-black border-amber-500/30 text-white placeholder:text-gray-500 focus:border-amber-500 focus:ring-amber-500 pr-12"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-amber-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="text-right mt-1">
                <Link to="/forgot-password" className="text-sm text-amber-400 hover:text-amber-300">
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full h-11 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black font-bold"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Signing In...
                </>
              ) : (
                t('auth.login')
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-sm text-gray-500 text-center mb-3">Don't have an account?</p>
            <Link to="/signup" className="block">
              <Button
                type="button"
                variant="outline"
                className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                data-testid="create-account-btn"
              >
                Create Account
              </Button>
            </Link>
            <p className="text-xs text-gray-500 text-center mt-3">
              Location staff? Contact your company admin for access.
            </p>
          </div>
        </div>
        )}

        <p className="text-center text-xs text-gray-500 mt-6">
          SlotFleet - Slot Machine Management Platform
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
